import React, { useEffect, useState, useRef } from 'react';
import {
  Button, Modal, Form, Input, Select, Tag, Space, Popconfirm, message,
  Drawer, Timeline, Typography, Tooltip, List, Collapse,
} from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import {
  PlusOutlined, DeleteOutlined, PlayCircleOutlined, StopOutlined, EyeOutlined, ReloadOutlined, EditOutlined,
} from '@ant-design/icons';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { agentTasksApi, employeesApi } from '../services/api-services';
import { useTheme } from '../contexts/ThemeContext';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const STATUS_COLOR: Record<string, string> = {
  pending: 'default', queued: 'processing', running: 'blue',
  success: 'success', failed: 'error', cancelled: 'warning',
};
const STATUS_LABEL: Record<string, string> = {
  pending: '待执行', queued: '排队中', running: '执行中',
  success: '已完成', failed: '已失败', cancelled: '已取消',
};
const EVENT_LABEL: Record<string, string> = {
  thinking: '思考', tool_call: '调用工具', error: '错误',
};

function mergeEvents(events: any[]): any[] {
  const merged: any[] = [];
  for (const e of events.filter(e => e.type !== 'output')) {
    const last = merged[merged.length - 1];
    if (last && last.type === e.type && e.type === 'thinking') {
      last.content += e.content;
    } else {
      merged.push({ ...e });
    }
  }
  return merged;
}

const RunDetail: React.FC<{ run: any; bgColor: string }> = ({ run, bgColor }) => (
  <div style={{ padding: '8px 0' }}>
    {run.result && (
      <div style={{ marginBottom: 12 }}>
        <Text strong>执行结果</Text>
        <Paragraph style={{ marginTop: 4, whiteSpace: 'pre-wrap', background: bgColor, padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
          {run.result}
        </Paragraph>
      </div>
    )}
    {run.error && (
      <div style={{ marginBottom: 12 }}>
        <Text strong type="danger">错误信息</Text>
        <Paragraph type="danger" style={{ marginTop: 4, fontSize: 13 }}>{run.error}</Paragraph>
      </div>
    )}
    {mergeEvents(run.events || []).length > 0 && (
      <div>
        <Text strong>执行过程</Text>
        <div style={{ marginTop: 8 }}>
          <Timeline
            items={mergeEvents(run.events).map((e: any, i: number) => ({
              key: i,
              color: e.type === 'error' ? 'red' : e.type === 'tool_call' ? 'blue' : 'gray',
              children: (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(e.t).toLocaleTimeString('zh-CN')} · {EVENT_LABEL[e.type] || e.type}
                  </Text>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginTop: 2 }}>{e.content}</div>
                </div>
              ),
            }))}
          />
        </div>
      </div>
    )}
  </div>
);

const AgentTaskList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [employees, setEmployees] = useState<any[]>([]);
  const [createVisible, setCreateVisible] = useState(false);
  const [editTask, setEditTask] = useState<any>(null);
  const [detailTask, setDetailTask] = useState<any>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [mdEditorOpen, setMdEditorOpen] = useState(false);
  const [mdEditorValue, setMdEditorValue] = useState('');
  const [form] = Form.useForm();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { theme } = useTheme();
  const bgColor = theme === 'glass' ? 'rgba(255,255,255,0.08)' : '#f5f5f5';
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const res: any = await agentTasksApi.list({ status: statusFilter, page, page_size: size });
      setData(res.items);
      setTotal(res.total);
    } catch { message.error('获取任务列表失败'); }
    setLoading(false);
  };

  useEffect(() => { setCurrentPage(1); fetchData(1, pageSize); }, [statusFilter]);
  useEffect(() => {
    employeesApi.list().then((res: any) => setEmployees(res)).catch(() => {});
  }, []);

  // 有运行中任务时自动轮询列表
  useEffect(() => {
    const hasActive = data.some(t => ['queued', 'running'].includes(t.status));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchData, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data]);

  const fetchRuns = async (taskId: string) => {
    setRunsLoading(true);
    try {
      const res: any = await agentTasksApi.getRuns(taskId);
      setRuns(res);
    } catch { message.error('获取执行记录失败'); }
    setRunsLoading(false);
  };

  const openDetail = (task: any) => {
    setDetailTask(task);
    setRuns([]);
    fetchRuns(task.task_id);
  };

  // 详情抽屉轮询（任务运行中时）
  useEffect(() => {
    if (!detailTask || !['queued', 'running'].includes(detailTask.status)) return;
    const t = setInterval(async () => {
      try {
        const res: any = await agentTasksApi.get(detailTask.task_id);
        setDetailTask(res);
        fetchRuns(detailTask.task_id);
        if (!['queued', 'running'].includes(res.status)) clearInterval(t);
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [detailTask?.task_id, detailTask?.status]);

  const handleCreate = async (values: any) => {
    try {
      await agentTasksApi.create(values);
      message.success('任务已创建');
      setCreateVisible(false);
      form.resetFields();
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '创建失败'); }
  };

  const openEdit = (task: any) => {
    setEditTask(task);
    form.setFieldsValue({
      title: task.title,
      employee_id: task.employee_id,
      instruction: task.instruction,
      schedule_type: task.schedule_type,
      schedule_at: task.schedule_at ? task.schedule_at.slice(0, 16) : undefined,
      cron_expr: task.cron_expr,
    });
    setMdEditorValue(task.instruction || '');
  };

  const handleUpdate = async (values: any) => {
    try {
      await agentTasksApi.update(editTask.task_id, values);
      message.success('任务已更新');
      setEditTask(null);
      form.resetFields();
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '更新失败'); }
  };

  const handleRun = async (taskId: string) => {
    try {
      await agentTasksApi.run(taskId);
      message.success('已触发执行');
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '触发失败'); }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await agentTasksApi.cancel(taskId);
      message.success('已取消');
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '取消失败'); }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await agentTasksApi.delete(taskId);
      message.success('已删除');
      if (detailTask?.task_id === taskId) setDetailTask(null);
      fetchData();
    } catch { message.error('删除失败'); }
  };

  const columns = [
    {
      title: '任务名称', dataIndex: 'title', key: 'title',
      render: (v: string, r: any) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openDetail(r)}>{v}</Button>
      ),
    },
    {
      title: '数字人', dataIndex: 'employee_name', key: 'employee_name', align: 'center' as const,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: '调度方式', dataIndex: 'schedule_type', key: 'schedule_type', align: 'center' as const,
      render: (v: string, r: any) => {
        if (v === 'cron') return (
          <Tooltip title={r.cron_expr}>
            <div><Tag>定时重复</Tag><div style={{ fontSize: 12, color: 'var(--muted-text)' }}>{r.cron_expr}</div></div>
          </Tooltip>
        );
        if (v === 'once') return (
          <Space direction="vertical" size={2} align="center">
            <Tag>单次定时</Tag>
            {r.schedule_at && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                🕐 {new Date(r.schedule_at).toLocaleString()}
              </Text>
            )}
          </Space>
        );
        return <Tag>立即执行</Tag>;
      },
    },
    {
      title: '状态', dataIndex: 'latest_run', key: 'latest_run', align: 'center' as const,
      render: (run: any, r: any) => {
        if (['queued', 'running'].includes(r.status)) {
          return <Tag color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Tag>;
        }
        if (!run) return <Tag color="default">未执行</Tag>;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={STATUS_COLOR[run.status]}>{STATUS_LABEL[run.status]}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {run.started_at ? new Date(run.started_at).toLocaleString() : ''}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '操作', key: 'actions', width: 220, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>详情</Button>
          {!['queued', 'running'].includes(r.status) && (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          )}
          {['success', 'failed', 'cancelled', 'pending'].includes(r.status) && (
            <Button type="link" size="small" icon={<PlayCircleOutlined />} onClick={() => handleRun(r.task_id)}>执行</Button>
          )}
          {['queued', 'running'].includes(r.status) && (
            <Popconfirm title="确认取消？" onConfirm={() => handleCancel(r.task_id)}>
              <Button type="link" size="small" danger icon={<StopOutlined />}>取消</Button>
            </Popconfirm>
          )}
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.task_id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">后台任务</h1>
      </div>
      <p className="apple-page-subtitle">管理数字人后台执行的任务，支持立即执行、定时执行和周期性任务。</p>

      <div className="apple-toolbar">
        <Select
          allowClear placeholder="状态筛选" style={{ width: 130 }}
          value={statusFilter} onChange={setStatusFilter}
          options={Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l }))}
        />
        <Space>
          <Button className="apple-btn-secondary" icon={<ReloadOutlined />} onClick={() => fetchData()}>刷新</Button>
          <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
            新建任务
          </Button>
        </Space>
      </div>

      <ResponsiveTable
        rowKey="task_id" columns={columns} dataSource={data}
        loading={loading}
        pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `共 ${t} 条`, onChange: (page, size) => { setCurrentPage(page); setPageSize(size); fetchData(page, size); } }}
      />

      {/* 创建/编辑任务 Modal */}
      <Modal
        className="apple-modal"
        title={editTask ? '编辑任务' : '新建后台任务'}
        open={createVisible || !!editTask}
        onCancel={() => { setCreateVisible(false); setEditTask(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editTask ? '保存' : '创建'}
        width={600}
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={editTask ? handleUpdate : handleCreate}>
          <Form.Item name="title" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input placeholder="给任务起个名字" />
          </Form.Item>
          <Form.Item name="employee_id" label="执行数字人" rules={[{ required: true, message: '请选择数字人' }]}>
            <Select placeholder="选择数字人" options={employees.map((e: any) => ({ value: e.id, label: e.name }))} />
          </Form.Item>
          <Form.Item name="instruction" label="执行指令" rules={[{ required: true, message: '请输入执行指令' }]}>
            <TextArea
              rows={3}
              placeholder="告诉数字人需要做什么..."
              onChange={e => setMdEditorValue(e.target.value)}
            />
          </Form.Item>
          <div style={{ marginTop: -16, marginBottom: 16, textAlign: 'right' }}>
            <Button
              size="small"
              type="link"
              onClick={() => { setMdEditorValue(form.getFieldValue('instruction') || ''); setMdEditorOpen(true); }}
            >
              放大编辑（Markdown）
            </Button>
          </div>
          <Form.Item name="schedule_type" label="调度方式" initialValue="immediate">
            <Select options={[
              { value: 'immediate', label: '立即执行' },
              { value: 'once', label: '定时执行（单次）' },
              { value: 'cron', label: '定时执行（重复）' },
            ]} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.schedule_type !== c.schedule_type}>
            {({ getFieldValue }) => {
              const t = getFieldValue('schedule_type');
              if (t === 'once') return (
                <Form.Item name="schedule_at" label="执行时间" rules={[{ required: true }]}>
                  <Input type="datetime-local" />
                </Form.Item>
              );
              if (t === 'cron') return (
                <Form.Item name="cron_expr" label="Cron 表达式" rules={[{ required: true }]} extra="例：0 9 * * 1-5（工作日早9点）">
                  <Input placeholder="0 9 * * 1-5" />
                </Form.Item>
              );
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 执行指令 Markdown 编辑器 */}
      <Modal
        open={mdEditorOpen}
        title="编辑执行指令（Markdown）"
        width={800}
        onCancel={() => setMdEditorOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setMdEditorOpen(false)}>取消</Button>,
          <Button key="confirm" type="primary" onClick={() => {
            form.setFieldValue('instruction', mdEditorValue);
            setMdEditorOpen(false);
          }}>确认</Button>,
        ]}
      >
        <MdEditor
          value={mdEditorValue}
          style={{ height: 420 }}
          renderHTML={text => require('react-dom/server').renderToStaticMarkup(
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
          )}
          onChange={({ text }) => setMdEditorValue(text)}
        />
      </Modal>

      {/* 任务详情 Drawer */}
      <Drawer
        title={detailTask?.title}
        open={!!detailTask}
        onClose={() => setDetailTask(null)}
        size="large"
        extra={detailTask && ['queued', 'running'].includes(detailTask.status) && (
          <Tag color="blue" style={{ marginRight: 0 }}>执行中...</Tag>
        )}
      >
        {detailTask && (
          <div>
            <Space style={{ marginBottom: 16 }} wrap>
              <Tag color={STATUS_COLOR[detailTask.status]}>{STATUS_LABEL[detailTask.status]}</Tag>
              {detailTask.employee_name && <Tag color="blue">{detailTask.employee_name}</Tag>}
              {detailTask.schedule_type === 'cron' && <Tag>定时重复：{detailTask.cron_expr}</Tag>}
            </Space>

            <div style={{ marginBottom: 16 }}>
              <Text strong>执行指令</Text>
              <Paragraph style={{ marginTop: 4, background: bgColor, padding: '8px 12px', borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                {detailTask.instruction}
              </Paragraph>
            </div>

            <div>
              <Text strong>执行历史（{runs.length} 次）</Text>
              <div style={{ marginTop: 8 }}>
                {runsLoading ? (
                  <div style={{ color: 'var(--muted-text)', padding: 16 }}>加载中...</div>
                ) : runs.length === 0 ? (
                  <div style={{ color: 'var(--muted-text)', padding: 16 }}>暂无执行记录</div>
                ) : (
                  <Collapse
                    size="small"
                    defaultActiveKey={runs[0]?.run_id}
                    items={runs.map((run: any) => ({
                      key: run.run_id,
                      label: (
                        <Space>
                          <Tag color={STATUS_COLOR[run.status]}>{STATUS_LABEL[run.status]}</Tag>
                          <Text style={{ fontSize: 13 }}>
                            {run.started_at ? new Date(run.started_at).toLocaleString() : ''}
                          </Text>
                          {run.finished_at && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              耗时 {Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)}s
                            </Text>
                          )}
                        </Space>
                      ),
                      children: <RunDetail run={run} bgColor={bgColor} />,
                    }))}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AgentTaskList;
